import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BeakerIcon, Cog6ToothIcon, TrashIcon, LanguageIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { usePermissions } from '@/hooks/usePermissions';
import { useSubscription } from '@/hooks/useSubscription';
import { analyticsApi } from '@/services/analyticsApi';
import { organizationApi } from '@/services/api';
import { CONTENT_TYPE_LABEL, ContentType } from '@/services/contentApi';

const ALL_CONTENT_TYPES = Object.keys(CONTENT_TYPE_LABEL) as ContentType[];

const SettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { hasPermission, isLoading } = usePermissions();
  const [seeding, setSeeding] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [savingPivot, setSavingPivot] = useState(false);
  const canManageSettings = hasPermission('settings', 'update') || hasPermission('settings', 'manage');
  const { data: subscription } = useSubscription();
  const hasTeamsFeature = subscription?.entitlements.approvalWorkflows === true;
  const [savingWorkflowType, setSavingWorkflowType] = useState<ContentType | null>(null);

  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationApi.getCurrent().then(res => res.data.data),
    enabled: canManageSettings,
  });
  // Orgs that predate this setting (or never explicitly set it) default to on,
  // matching the pivot's existing always-on behavior.
  const pivotEnabled = organization?.settings?.videoTranslation?.pivotThroughEnglish !== false;

  const handleTogglePivot = async () => {
    setSavingPivot(true);
    try {
      await organizationApi.updateSettings({
        settings: { videoTranslation: { pivotThroughEnglish: !pivotEnabled } },
      });
      await queryClient.invalidateQueries({ queryKey: ['organization'] });
      toast.success(`English pivot ${!pivotEnabled ? 'enabled' : 'disabled'}`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update translation setting');
    } finally {
      setSavingPivot(false);
    }
  };

  const workflowMap = organization?.settings?.workflow?.requireApprovedReviewBeforeReassign ?? {};

  const handleToggleWorkflow = async (contentType: ContentType) => {
    setSavingWorkflowType(contentType);
    try {
      // The backend's settings merge is shallow, so the full current map
      // must be sent back with just this one key changed — otherwise every
      // other content type's toggle would be reset to its default.
      const next = { ...workflowMap, [contentType]: !workflowMap[contentType] };
      await organizationApi.updateSettings({
        settings: { workflow: { requireApprovedReviewBeforeReassign: next } },
      });
      await queryClient.invalidateQueries({ queryKey: ['organization'] });
      toast.success('Review workflow setting updated');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update review workflow setting');
    } finally {
      setSavingWorkflowType(null);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const result = await analyticsApi.seedDemo(90);
      toast.success(`Seeded ${result.created} demo metric rows`);
      await queryClient.invalidateQueries({ queryKey: ['analytics'] });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to seed analytics demo data');
    } finally {
      setSeeding(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear all demo analytics data? Real synchronized YouTube data will not be affected.')) return;
    setClearing(true);
    try {
      const result = await analyticsApi.clearDemo();
      toast.success(`Cleared ${result.deleted} demo metric rows`);
      await queryClient.invalidateQueries({ queryKey: ['analytics'] });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to clear analytics demo data');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-xl bg-blue-100 p-2.5 dark:bg-blue-900/30">
          <Cog6ToothIcon className="h-7 w-7 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Organization configuration and administrative tools.</p>
        </div>
      </div>

      {!isLoading && canManageSettings && (
        <div className="space-y-6">
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 px-6 py-5 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <LanguageIcon className="h-6 w-6 text-indigo-500" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Video translation</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Controls how the video translation pipeline handles text translation.</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Pivot through English</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-xl">
                When translating between two non-English languages, translate source → English → target instead of a single direct hop.
                Direct non-English-to-non-English translation is often unreliable (especially via Sarvam, whose models are tuned around
                Indian-language↔English pairs). Turning this off makes the pipeline attempt a single direct translation instead.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={pivotEnabled}
              onClick={handleTogglePivot}
              disabled={savingPivot}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                pivotEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                pivotEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </section>

        {hasTeamsFeature && (
          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="border-b border-gray-200 px-6 py-5 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <ClipboardDocumentCheckIcon className="h-6 w-6 text-amber-500" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Review workflow</h2>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    For each content type, require at least one approved review before it can be reassigned to a different team. Off by default.
                  </p>
                </div>
              </div>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {ALL_CONTENT_TYPES.map((contentType) => {
                const enabled = workflowMap[contentType] === true;
                return (
                  <div key={contentType} className="flex items-center justify-between px-6 py-4">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{CONTENT_TYPE_LABEL[contentType]}</p>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={enabled}
                      onClick={() => handleToggleWorkflow(contentType)}
                      disabled={savingWorkflowType === contentType}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                        enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        enabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 px-6 py-5 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <BeakerIcon className="h-6 w-6 text-purple-500" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Analytics demo data</h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Administrative test data for validating analytics dashboards. Production YouTube synchronization does not require demo data.</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Demo dataset management</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Seed 90 days of synthetic metrics or remove only records marked as demo.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={handleSeed} disabled={seeding || clearing} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                <BeakerIcon className="h-4 w-4" />
                {seeding ? 'Seeding…' : 'Seed demo data'}
              </button>
              <button type="button" onClick={handleClear} disabled={clearing || seeding} className="inline-flex items-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20">
                <TrashIcon className="h-4 w-4" />
                {clearing ? 'Clearing…' : 'Clear demo data'}
              </button>
            </div>
          </div>
        </section>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
